// Programas anuales (lote C, M48): lógica pura, sin base de datos.
//  - Contenido congelado de una versión (lo que se firma) y su SHA-256 con la forma canónica de las firmas (M45).
//  - Plantillas: las 12 actividades del programa psicosocial (de psicosocial-programa.ts, Anexo 1 de la Guía del MDT) y
//    las 14 del de alcohol, tabaco y otras drogas (Matriz de actividades MSP-MDT 2020, Acuerdo Interministerial
//    MDT-MSP-2019-0038), cada una con un indicador opcional.
//  - PO 2: cronograma de exámenes desde Trabajadores y su último examen.
//  - PO 14: cronograma de inspecciones desde las programaciones de Inspecciones v2 (plantilla, área o sede, frecuencia).
//  - Respaldo de cada actividad: ejecución registrada (archivo o vínculo), capacitación vinculada realizada (TH 8),
//    examen hecho (PO 2), evidencia del seguimiento psicosocial del mismo año y número (PO 24) o inspección firmada de
//    la misma programación (PO 14).
//  - Estado del ítem del Checklist (Pendiente, No cumple, En proceso, Cumple) y del registro en el SUT.
// Reglas DECIDIDAS POR CLAUDE, por validar con Kevin (en pantalla y PDF: "sugeridos por StorSafe, no normativos"):
//  - Pendiente: no hay programa del año en curso.
//  - No cumple: no hay versión aprobada y ya pasó el 31 de enero; o una actividad lleva más de 30 días vencida sin
//    ejecutar.
//  - En proceso: aprobado, con actividades vencidas hace 30 días o menos; también un programa sin aprobar antes del
//    1 de febrero (se está preparando: el Anexo no lo dice y el cálculo no lo castiga).
//  - Cumple: aprobado, y todas las actividades con fecha pasada están ejecutadas con respaldo.
//  - SUT (PO 25 y PO 27): Cumple con la constancia del año en curso; si no, Pendiente.

import { canonico } from '../firmas/firmas-logica';
import { diaEc, fechaEc } from '../fechas';
import { fechaN } from '../inspecciones/insp-logica';
import { ETIQUETA_FRECUENCIA } from '../inspecciones/insp-tipos';
import { sha256Hex } from '../psicosocial-codigos';
import { PROGRAMA_ACTIVITIES } from '../psicosocial-programa';
import type { Traza } from '../pe/pe-tipos';
import type { EstadoItem } from '../plan-emergencias-compliance-sync';
import { plural } from '../plurales';
import {
  CONFIG, DIA_LIMITE_APROBACION, DIAS_ANTICIPACION_EXAMEN, DIAS_TOLERANCIA_EJECUCION, ETIQUETA_EXAMEN, FUENTE_ANEXO1, MES_LIMITE_APROBACION,
  NORMA_PLAZOS, NOTA_PLAZOS, PERIODICIDAD_EXAMENES_DEFECTO,
  type ActividadContenido, type ActividadEditable, type AjustesPrograma, type CapacitacionMin, type ContenidoPrograma, type ExamenMin,
  type FilaActividad, type FilaEjecucion, type FilaFirmaPrograma, type FilaPrograma, type FilaSut, type FilaVersion, type InspeccionMin,
  type ProgramacionMin, type RolFirmaPrograma, type TipoExamen, type TipoPrograma, type TrabajadorMin,
} from './prog-tipos';

// ── Fechas (AAAA-MM-DD, sin zona horaria) ──
const RE_FECHA = /^(\d{4})-(\d{2})-(\d{2})$/;
const aUtc = (f: string) => { const m = RE_FECHA.exec(f); return m ? Date.UTC(+m[1], +m[2] - 1, +m[3]) : NaN; };
const deUtc = (ms: number) => new Date(ms).toISOString().slice(0, 10);
export const isoLocal = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
/** Días de a hasta b (b - a). */
export const diasEntre = (a: string, b: string) => Math.round((aUtc(b) - aUtc(a)) / 86_400_000);
export const sumarDias = (f: string, n: number) => deUtc(aUtc(f) + n * 86_400_000);
export function sumarMeses(f: string, n: number): string {
  const m = RE_FECHA.exec(f);
  if (!m) return f;
  const anio = +m[1], mes = +m[2] - 1 + n, dia = +m[3];
  const ultimo = new Date(Date.UTC(anio, mes + 1, 0)).getUTCDate();
  return deUtc(Date.UTC(anio, mes, Math.min(dia, ultimo)));
}
// dd/mm/aaaa: una fecha sin hora, tal cual; un instante (approved_at), en el día de Ecuador (lib/fechas).
export const fechaCorta = (iso: string | null | undefined) => fechaEc(iso);
/** Días para aprobar el programa del año de registro, si la empresa se registró después del 31 de enero (sugerido). */
export const DIAS_APROBACION_EMPRESA_NUEVA = 30;
/** Día de registro de la empresa (companies.created_at) en la hora local. */
export const diaRegistro = (registrada: string | null | undefined): string | null => {
  if (!registrada) return null;
  if (registrada.length <= 10) return registrada.slice(0, 10);
  const d = new Date(registrada);
  return Number.isNaN(d.getTime()) ? null : isoLocal(d);
};
/** Fecha límite sugerida para aprobar el programa del año: el 31 de enero; el año en que la empresa se registró, si fue
 *  después del 31 de enero, 30 días desde el registro (los cinco programas). */
export function limiteAprobacion(anio: number, registrada?: string | null): string {
  const general = `${anio}-${String(MES_LIMITE_APROBACION).padStart(2, '0')}-${String(DIA_LIMITE_APROBACION).padStart(2, '0')}`;
  const dia = diaRegistro(registrada);
  if (!dia || Number(dia.slice(0, 4)) !== anio || dia <= general) return general;
  const [a, m, d] = dia.split('-').map(Number);
  return deUtc(Date.UTC(a, m - 1, d + DIAS_APROBACION_EMPRESA_NUEVA));
}

// ── Contenido congelado y hash ──
const texto = (s: string | null | undefined) => (s ?? '').trim();

export function contenidoVersion(tipo: TipoPrograma, empresa: string, anio: number, v: Pick<FilaVersion, 'id' | 'version' | 'objectives'
  | 'scope_diagnosis' | 'methodology' | 'responsibles' | 'resources' | 'settings'>, actividades: FilaActividad[]): ContenidoPrograma {
  const ajustes: AjustesPrograma = tipo === 'examenes'
    ? { periodicidad_meses: v.settings?.periodicidad_meses ?? PERIODICIDAD_EXAMENES_DEFECTO } : {};
  return {
    tipo, empresa: empresa.trim(), anio, version: v.version, version_id: v.id,
    objetivos: texto(v.objectives), alcance_diagnostico: texto(v.scope_diagnosis), metodologia: texto(v.methodology),
    responsables: texto(v.responsibles), recursos: texto(v.resources), ajustes,
    actividades: ordenarActividades(actividades).map((a, i): ActividadContenido => ({
      clave: a.activity_key, orden: i + 1, numero_lista: a.template_no ?? null, actividad: texto(a.title), destinatarios: a.audience_kind,
      destinatarios_texto: textoDestinatarios(a), fecha_planificada: a.planned_date, responsable: texto(a.responsible),
      duracion_horas: a.duration_hours === null || a.duration_hours === undefined ? null : Number(a.duration_hours),
      capacitacion_id: a.training_id ?? null, tipo_examen: a.exam_type ?? null, notas: texto(a.notes),
      indicador: texto(a.indicator), programacion_id: a.schedule_id ?? null,
    })),
  };
}

export const hashPrograma = (c: ContenidoPrograma): Promise<string> => sha256Hex(canonico(c));

export interface FirmantePrograma { rol: RolFirmaPrograma; nombre: string; cedula: string }
export const textoFirmaPrograma = (hashContenido: string, f: FirmantePrograma) =>
  canonico({ contenido: hashContenido, firmante: { rol: f.rol, nombre: f.nombre.trim(), cedula: f.cedula.trim() } });
export const hashFirmaPrograma = (hashContenido: string, f: FirmantePrograma) => sha256Hex(textoFirmaPrograma(hashContenido, f));

export function ordenarActividades<T extends Pick<FilaActividad, 'planned_date' | 'position' | 'template_no' | 'activity_key'>>(a: T[]): T[] {
  return [...a].sort((x, y) => (x.template_no ?? 99) - (y.template_no ?? 99) || x.planned_date.localeCompare(y.planned_date)
    || x.position - y.position || x.activity_key.localeCompare(y.activity_key));
}

export function textoDestinatarios(a: Pick<FilaActividad, 'audience_kind' | 'audience_label'>): string {
  if (a.audience_kind === 'todos') return 'Todos los trabajadores';
  const l = texto(a.audience_label);
  return a.audience_kind === 'puesto' ? (l ? `Puesto: ${l}` : 'Un puesto') : (l || 'Un trabajador');
}

// ── Lo que falta para preparar y para aprobar ──
export const cedulaValida = (c: string | null | undefined) => /^\d{10}$/.test((c ?? '').trim());

export function faltantesParaPreparar(tipo: TipoPrograma, v: Pick<FilaVersion, 'objectives' | 'scope_diagnosis' | 'methodology'
  | 'responsibles' | 'resources'>, actividades: FilaActividad[]): string[] {
  const f: string[] = [];
  if (!texto(v.objectives)) f.push('Escriba los objetivos del programa.');
  if (!texto(v.scope_diagnosis)) f.push(tipo === 'capacitacion' ? 'Escriba el alcance y el diagnóstico de necesidades.'
    : tipo === 'inspecciones' ? 'Escriba el alcance y las áreas a inspeccionar.' : 'Escriba el alcance y el diagnóstico.');
  if (!texto(v.methodology)) f.push(tipo === 'inspecciones' ? 'Escriba la lista de verificación (las plantillas que se usan).' : 'Escriba la metodología.');
  if (!texto(v.responsibles)) f.push('Escriba los responsables.');
  if (tipo === 'capacitacion' && !texto(v.resources)) f.push('Escriba los recursos y el material utilizado.');
  if (actividades.length === 0) f.push('Agregue al menos una actividad al cronograma.');
  actividades.forEach(a => {
    if (texto(a.title).length < 3) f.push(`Una actividad no tiene nombre (fecha ${fechaCorta(a.planned_date)}).`);
    if (!RE_FECHA.test(a.planned_date ?? '')) f.push(`La actividad "${texto(a.title)}" no tiene fecha planificada.`);
    if (a.audience_kind !== 'todos' && !texto(a.audience_label)) f.push(`Indique los destinatarios de "${texto(a.title)}".`);
    if (tipo === 'capacitacion' && !texto(a.responsible)) f.push(`Indique el responsable de "${texto(a.title)}".`);
    if (tipo === 'capacitacion' && (a.duration_hours === null || a.duration_hours === undefined)) f.push(`Indique la duración de "${texto(a.title)}".`);
    if (tipo === 'examenes' && !a.exam_type) f.push(`Indique el tipo de examen de "${texto(a.title)}".`);
  });
  return f;
}

export function faltantesParaAprobar(metodo: 'pantalla' | 'papel', firmas: Pick<FilaFirmaPrograma, 'role'>[], conEscaneo: boolean): string[] {
  if (metodo === 'papel') return conEscaneo ? [] : ['Suba el escaneo del programa firmado.'];
  const f: string[] = [];
  if (!firmas.some(x => x.role === 'responsable_sso')) f.push('Falta la firma del responsable de seguridad y salud en el trabajo.');
  if (!firmas.some(x => x.role === 'representante_legal')) f.push('Falta la firma del representante legal.');
  return f;
}

// ── Plantillas ──
/** PO 26: las 14 actividades de la Matriz de actividades del Programa de Prevención Integral del uso y consumo de alcohol,
 *  tabaco u otras drogas en los espacios laborales (MSP y MDT, 2020; Acuerdo Interministerial MDT-MSP-2019-0038). */
export const ACTIVIDADES_SUSTANCIAS: readonly string[] = [
  'Sensibilizar a toda la población trabajadora de cada centro de trabajo sobre la prevención integral del uso y consumo de alcohol, tabaco u otras drogas.',
  'Socializar a toda la población trabajadora la ejecución del programa de prevención integral.',
  'Realizar el diagnóstico general de la situación de consumo de alcohol, tabaco u otras drogas de la población trabajadora de cada centro de trabajo.',
  'Realizar en cada centro de trabajo campañas informativas sobre prácticas de vida saludable.',
  'Colocar señalización informativa, obligatoria, preventiva o prohibitiva sobre la prevención del uso y consumo de alcohol, tabaco u otras drogas.',
  'Gestionar la certificación de "Espacio 100 % libre de humo de tabaco" del Ministerio de Salud Pública.',
  'Realizar talleres, conversatorios o video foros sobre prevención integral del uso y consumo de drogas identificados en el diagnóstico.',
  'Realizar talleres, conversatorios o video foros sobre el fortalecimiento de factores protectores individuales, colectivos y sociales.',
  'Realizar actividades físico-recreativas y promover el buen uso del ocio y del tiempo libre.',
  'Implementar medidas de control para prevenir situaciones de riesgo que afecten la integridad de la población trabajadora y la seguridad de los centros de trabajo.',
  'Derivar a atención especializada a la población trabajadora con presunción de uso o consumo problemático de drogas.',
  'Monitorear el cumplimiento del tratamiento por parte de la población trabajadora que accedió a él.',
  'Elaborar los procedimientos o protocolos integrales para la reinserción laboral de quienes están o estuvieron en tratamiento por consumo de drogas.',
  'Gestionar la reinserción laboral de quienes culminaron el tratamiento por consumo de drogas.',
];
export const EJEMPLO_INDICADOR = 'Ej.: trabajadores sensibilizados / total de trabajadores × 100';

const BASE_ACTIVIDAD = { audience_kind: 'todos' as const, audience_label: null, position_id: null, worker_id: null, responsible: null,
  duration_hours: null, training_id: null, exam_type: null, indicator: null, schedule_id: null };

export function plantillaActividades(tipo: TipoPrograma, anio: number): ActividadEditable[] {
  if (tipo === 'psicosocial') {
    return PROGRAMA_ACTIVITIES.map(a => ({ ...BASE_ACTIVIDAD, template_no: a.no, title: a.label, planned_date: `${anio}-12-15`,
      notes: `Respaldo: ${a.verificationNote}` }));
  }
  if (tipo === 'sustancias') {
    return ACTIVIDADES_SUSTANCIAS.map((titulo, i) => ({ ...BASE_ACTIVIDAD, template_no: i + 1, title: titulo, planned_date: `${anio}-12-15`, notes: null }));
  }
  return [];
}

export const AVISO_LISTA_PSICOSOCIAL = 'Las 12 actividades vienen del Anexo 1 de la Guía para la implementación del programa de prevención de '
  + 'riesgos psicosociales del Ministerio del Trabajo (redacción resumida). Ajuste las fechas y los responsables.';

// ── PO 2: cronograma de exámenes desde Trabajadores ──
export interface ExamenPlanificado extends ActividadEditable { worker_id: string; exam_type: TipoExamen }

/**
 * El cronograma se arma como al inicio del año (así los exámenes ya hechos en el año cuentan como ejecución). Para cada
 * trabajador no desvinculado (o desvinculado con salida en el año): ingreso en su fecha de entrada si entra en el año;
 * periódico según el último examen anterior al año y la periodicidad (puede haber más de uno en el año); periódico al
 * inicio del año si nunca tuvo examen o el último ya venció; retiro si sale en el año. El reintegro se agrega a mano
 * (depende de una ausencia).
 */
export function cronogramaExamenes(anio: number, trabajadores: TrabajadorMin[], examenes: ExamenMin[], periodicidadMeses: number): ExamenPlanificado[] {
  const inicio = `${anio}-01-01`, fin = `${anio}-12-31`;
  const meses = Math.max(1, Math.min(60, Math.round(periodicidadMeses || PERIODICIDAD_EXAMENES_DEFECTO)));
  const ultimoAntes = new Map<string, string>();
  const anotar = (w: string, f: string | null | undefined) => {
    if (!f || !RE_FECHA.test(f) || f >= inicio) return;
    const u = ultimoAntes.get(w);
    if (!u || f > u) ultimoAntes.set(w, f);
  };
  examenes.forEach(e => { if (e.worker_id) anotar(e.worker_id, e.exam_date); });
  trabajadores.forEach(w => anotar(w.id, w.last_medical_exam));
  const out: ExamenPlanificado[] = [];
  const poner = (w: TrabajadorMin, tipo: TipoExamen, fecha: string) => out.push({
    title: `Examen ${ETIQUETA_EXAMEN[tipo].toLowerCase()}: ${w.full_name.trim()}`, audience_kind: 'trabajador',
    audience_label: w.position?.trim() ? `${w.full_name.trim()} (${w.position.trim()})` : w.full_name.trim(), position_id: null, worker_id: w.id,
    planned_date: fecha, responsible: null, duration_hours: null, training_id: null, exam_type: tipo, notes: null, template_no: null,
    indicator: null, schedule_id: null,
  });
  const periodicos = (w: TrabajadorMin, desde: string, limite: string) => {
    for (let f = desde; f <= limite; f = sumarMeses(f, meses)) poner(w, 'periodico', f);
  };
  [...trabajadores].sort((a, b) => a.full_name.localeCompare(b.full_name, 'es')).forEach(w => {
    const sale = w.end_date && RE_FECHA.test(w.end_date) && w.end_date >= inicio && w.end_date <= fin ? w.end_date : null;
    if (w.status === 'desvinculado' && !sale) return;
    const entra = w.start_date && RE_FECHA.test(w.start_date) && w.start_date >= inicio && w.start_date <= fin ? w.start_date : null;
    const limite = sale ?? fin;
    const ultimo = ultimoAntes.get(w.id);
    if (entra) {
      poner(w, 'pre_ocupacional', entra);
      periodicos(w, sumarMeses(entra, meses), limite);
    } else if (!ultimo) {
      periodicos(w, inicio, limite);
    } else {
      const siguiente = sumarMeses(ultimo, meses);
      periodicos(w, siguiente < inicio ? inicio : siguiente, limite);
    }
    if (sale) poner(w, 'retiro', sale);
  });
  return out.sort((a, b) => a.planned_date.localeCompare(b.planned_date) || (a.audience_label ?? '').localeCompare(b.audience_label ?? '', 'es'));
}

// ── PO 14: cronograma de inspecciones desde las programaciones de Inspecciones v2 ──
export interface NombresProgramacion { plantillas: Record<string, string>; sedes: Record<string, string>; trabajadores: Record<string, string>; varias: boolean }

/** Cada fecha del año de cada programación activa (las mismas fechas que genera Inspecciones v2, fechaN): una actividad
 *  por inspección, con la programación de origen (su inspección firmada la ejecuta). Plantilla, área y sede en el título;
 *  la frecuencia en las notas; el responsable de la programación. */
export function cronogramaInspecciones(anio: number, programaciones: ProgramacionMin[], n: NombresProgramacion): ActividadEditable[] {
  const inicio = `${anio}-01-01`, fin = `${anio}-12-31`;
  const out: ActividadEditable[] = [];
  programaciones.filter(p => p.active).forEach(p => {
    const plantilla = (n.plantillas[p.template_id] ?? 'Inspección').trim();
    const sede = p.site_id ? n.sedes[p.site_id] : undefined;
    const titulo = [plantilla, p.area?.trim() || null, n.varias && sede ? sede : null].filter(Boolean).join(' · ').slice(0, 300);
    const responsable = p.responsible_role?.trim() || (p.responsible_worker_id ? n.trabajadores[p.responsible_worker_id] : '') || null;
    for (let k = 0; k < 1000; k++) {
      const f = fechaN(p.start_date, p.frequency, k);
      if (!f || f > fin || (p.end_date && f > p.end_date)) break;
      if (f < inicio) continue;
      out.push({ ...BASE_ACTIVIDAD, title: titulo, planned_date: f, responsible: responsable, schedule_id: p.id, template_no: null,
        notes: `Programación ${ETIQUETA_FRECUENCIA[p.frequency].toLowerCase()}` });
    }
  });
  return out.sort((a, b) => a.planned_date.localeCompare(b.planned_date) || a.title.localeCompare(b.title, 'es'));
}

// ── Respaldo de cada actividad ──
export type TipoRespaldo = 'archivo' | 'vinculo' | 'capacitacion' | 'examen' | 'psicosocial' | 'inspeccion';
export interface Respaldo { fecha: string; tipo: TipoRespaldo; texto: string; ejecucion: FilaEjecucion | null; trainingId?: string; examId?: string; inspectionId?: string }

export interface FuentesRespaldo {
  ejecuciones: FilaEjecucion[];
  capacitaciones: CapacitacionMin[];
  examenes: ExamenMin[];
  /** PO 24: número de actividad (1 a 12) → fecha de la primera evidencia vigente del seguimiento psicosocial del año. */
  evidenciaPsicosocial?: Record<number, string>;
  /** PO 14: inspecciones de Inspecciones v2 (solo cuentan las firmadas). */
  inspecciones?: InspeccionMin[];
}

/** PO 14: una inspección firmada de la misma programación ejecuta la actividad: la de la misma fecha programada o, si no,
 *  la primera hecha desde 15 días antes hasta 30 días después de la fecha planificada. Cada inspección, una actividad. */
export const DIAS_ANTES_INSPECCION = 15;
export const textoInspeccion = (i: InspeccionMin) => `Inspección firmada: ${i.template_name.trim()} (${fechaCorta(i.performed_date ?? i.signed_at)})`;

export const textoCapacitacion = (c: CapacitacionMin) => `Capacitación realizada: ${c.title.trim()} (${fechaCorta(c.scheduled_date)})`;
export const textoExamen = (e: ExamenMin, nombre: string) =>
  `Examen ${ETIQUETA_EXAMEN[e.exam_type as TipoExamen]?.toLowerCase() ?? 'médico'} de ${nombre} (${fechaCorta(e.exam_date)})`;

type ActividadRespaldo = Pick<FilaActividad, 'activity_key' | 'planned_date' | 'training_id' | 'worker_id' | 'exam_type' | 'template_no'
  | 'audience_label'> & Partial<Pick<FilaActividad, 'schedule_id'>>;

export function respaldos(tipo: TipoPrograma, anio: number, actividades: ActividadRespaldo[], f: FuentesRespaldo): Map<string, Respaldo> {
  const out = new Map<string, Respaldo>();
  const porClave = new Map(f.ejecuciones.map(e => [e.activity_key, e]));
  const caps = new Map(f.capacitaciones.map(c => [c.id, c]));
  const usados = new Set(f.ejecuciones.map(e => e.medical_exam_id).filter((x): x is string => !!x));
  const pendientesExamen: ActividadRespaldo[] = [];
  const pendientesInspeccion: ActividadRespaldo[] = [];
  actividades.forEach(a => {
    const e = porClave.get(a.activity_key);
    if (e && (e.evidence_path || (e.linked_label ?? '').trim())) {
      out.set(a.activity_key, { fecha: e.executed_date, tipo: e.evidence_path ? 'archivo' : 'vinculo',
        texto: e.evidence_path ? (e.linked_label?.trim() ? `${e.linked_label.trim()} · ${e.evidence_name ?? 'archivo'}` : e.evidence_name ?? 'Archivo de evidencia')
          : (e.linked_label ?? '').trim(), ejecucion: e });
      return;
    }
    if (a.training_id) {
      const c = caps.get(a.training_id);
      if (c && c.status === 'realizada') {
        out.set(a.activity_key, { fecha: c.scheduled_date, tipo: 'capacitacion', texto: textoCapacitacion(c), ejecucion: null, trainingId: c.id });
        return;
      }
    }
    if (tipo === 'psicosocial' && a.template_no && f.evidenciaPsicosocial?.[a.template_no]) {
      out.set(a.activity_key, { fecha: f.evidenciaPsicosocial[a.template_no], tipo: 'psicosocial', ejecucion: null,
        texto: `Evidencia del seguimiento del programa psicosocial ${anio} (actividad ${a.template_no})` });
      return;
    }
    if (tipo === 'examenes' && a.worker_id) pendientesExamen.push(a);
    if (tipo === 'inspecciones' && a.schedule_id) pendientesInspeccion.push(a);
  });
  // PO 14: la inspección firmada de la misma programación (misma fecha programada o, si no, hecha cerca de la fecha).
  const firmadas = (f.inspecciones ?? []).filter(i => !!i.signed_at && i.status === 'completada');
  const usadasInsp = new Set<string>();
  const ejecutadaEl = (i: InspeccionMin) => diaEc(i.performed_date ?? (i.signed_at as string));
  const tomar = (a: ActividadRespaldo, i: InspeccionMin) => {
    usadasInsp.add(i.id);
    out.set(a.activity_key, { fecha: ejecutadaEl(i), tipo: 'inspeccion', texto: textoInspeccion(i), ejecucion: null, inspectionId: i.id });
  };
  const sinExacta: ActividadRespaldo[] = [];
  ordenarPorFecha(pendientesInspeccion).forEach(a => {
    const exacta = firmadas.find(i => i.schedule_id === a.schedule_id && i.scheduled_date === a.planned_date && !usadasInsp.has(i.id));
    if (exacta) tomar(a, exacta); else sinExacta.push(a);
  });
  sinExacta.forEach(a => {
    const desde = sumarDias(a.planned_date, -DIAS_ANTES_INSPECCION), hasta = sumarDias(a.planned_date, DIAS_TOLERANCIA_EJECUCION);
    const cand = firmadas.filter(i => i.schedule_id === a.schedule_id && !usadasInsp.has(i.id) && ejecutadaEl(i) >= desde && ejecutadaEl(i) <= hasta)
      .sort((x, y) => ejecutadaEl(x).localeCompare(ejecutadaEl(y)))[0];
    if (cand) tomar(a, cand);
  });
  // PO 2: cada examen respalda una sola actividad (la más temprana que cubre), del mismo trabajador y tipo, en el año
  // del programa y desde 60 días antes de la fecha planificada.
  const inicio = `${anio}-01-01`, fin = `${anio}-12-31`;
  ordenarPorFecha(pendientesExamen).forEach(a => {
    const candidato = [...f.examenes]
      .filter(x => x.worker_id === a.worker_id && !usados.has(x.id) && (!a.exam_type || x.exam_type === a.exam_type)
        && x.exam_date >= inicio && x.exam_date <= fin && x.exam_date >= sumarDias(a.planned_date, -DIAS_ANTICIPACION_EXAMEN))
      .sort((x, y) => x.exam_date.localeCompare(y.exam_date))[0];
    if (candidato) {
      usados.add(candidato.id);
      const nombre = (a.audience_label ?? '').replace(/\s*\(.*\)$/, '').trim() || 'el trabajador';
      out.set(a.activity_key, { fecha: candidato.exam_date, tipo: 'examen', texto: textoExamen(candidato, nombre), ejecucion: null, examId: candidato.id });
    }
  });
  return out;
}
const ordenarPorFecha = <T extends { planned_date: string; activity_key: string }>(a: T[]) =>
  [...a].sort((x, y) => x.planned_date.localeCompare(y.planned_date) || x.activity_key.localeCompare(y.activity_key));

/** Respaldos que vienen de un registro vinculado y todavía no tienen fila de ejecución: se guardan para conservarlos
 *  (la inspección firmada queda como vínculo con su descripción, sin FK). */
export function respaldosPorGuardar(r: Map<string, Respaldo>): { activity_key: string; executed_date: string; training_id: string | null;
  medical_exam_id: string | null; linked_label: string }[] {
  return Array.from(r.entries()).filter(([, x]) => !x.ejecucion && (x.tipo === 'capacitacion' || x.tipo === 'examen' || x.tipo === 'inspeccion'))
    .map(([k, x]) => ({ activity_key: k, executed_date: x.fecha, training_id: x.trainingId ?? null, medical_exam_id: x.examId ?? null, linked_label: x.texto }));
}

// ── Estado de cada actividad y del programa ──
export type EstadoActividad = 'ejecutada' | 'por_venir' | 'vencida' | 'vencida_grave';
export const ETIQUETA_ESTADO_ACTIVIDAD: Record<EstadoActividad, string> = {
  ejecutada: 'Ejecutada', por_venir: 'Planificada', vencida: 'Vencida', vencida_grave: `Vencida hace más de ${DIAS_TOLERANCIA_EJECUCION} días`,
};

export function estadoActividad(planificada: string, respaldo: Respaldo | undefined, hoyIso: string): EstadoActividad {
  if (respaldo) return 'ejecutada';
  if (planificada >= hoyIso) return 'por_venir';
  return diasEntre(planificada, hoyIso) > DIAS_TOLERANCIA_EJECUCION ? 'vencida_grave' : 'vencida';
}

export interface EntradaEstado {
  tipo: TipoPrograma;
  programa: FilaPrograma | null;            // programa del año en curso
  hayVersion: boolean;                      // alguna versión (borrador, en firma o aprobada)
  aprobada: FilaVersion | null;
  actividades: ActividadRespaldo[];         // de la versión aprobada
  respaldos: Map<string, Respaldo>;
  empresaRegistrada?: string | null;        // companies.created_at (plazo de la empresa nueva)
}

export interface ResultadoPrograma {
  estado: EstadoItem; resumen: string; trazas: Traza[];
  total: number; conFechaPasada: number; ejecutadas: number; vencidas: number; vencidasGraves: number;
}

const estadoTraza = (e: EstadoItem): Traza['estado'] => (e === 'cumple' ? 'cumple' : e === 'no_cumple' ? 'no_cumple' : 'por_validar');

export function resultadoPrograma(e: EntradaEstado, hoy: Date = new Date()): ResultadoPrograma {
  const cfg = CONFIG[e.tipo];
  const hoyIso = isoLocal(hoy);
  const anio = hoy.getFullYear();
  const limite = limiteAprobacion(anio, e.empresaRegistrada);
  const registro = diaRegistro(e.empresaRegistrada);
  const plazoNuevo = !!registro && limite !== limiteAprobacion(anio);
  const articulo = `${cfg.item.replace('-0', ' ').replace('-', ' ')}: ${cfg.titulo.toLowerCase()}`;
  const vacio = { total: 0, conFechaPasada: 0, ejecutadas: 0, vencidas: 0, vencidasGraves: 0 };
  const trazaAprobacion = (estado: EstadoItem, nota: string): Traza => ({
    metodo: 'programa_anual', norma: NORMA_PLAZOS, articulo_o_tabla: plazoNuevo
      ? `Plazo de aprobación: ${fechaCorta(limite)}, empresa registrada el ${fechaCorta(registro)} (${DIAS_APROBACION_EMPRESA_NUEVA} días desde el registro)`
      : `Versión aprobada y firmada hasta el ${fechaCorta(limite)}`,
    umbral: 1, valor: e.aprobada ? 1 : 0, unidad: 'versión aprobada', estado: estadoTraza(estado), fuente_doc: FUENTE_ANEXO1, nota,
  });
  if (!e.programa) {
    const resumen = `No hay ${cfg.corto.toLowerCase()} del año ${anio}.`;
    return { estado: 'pendiente', resumen, ...vacio, trazas: [trazaAprobacion('pendiente', `${resumen} ${NOTA_PLAZOS}`)] };
  }
  if (!e.aprobada) {
    const vencido = hoyIso > limite;
    const estado: EstadoItem = vencido ? 'no_cumple' : 'en_proceso';
    const resumen = vencido
      ? `El ${cfg.corto.toLowerCase()} ${anio} no tiene una versión aprobada y firmada; el plazo sugerido era el ${fechaCorta(limite)}.`
      : `El ${cfg.corto.toLowerCase()} ${anio} está en preparación: apruébelo y fírmelo hasta el ${fechaCorta(limite)} (plazo sugerido).`;
    return { estado, resumen, ...vacio, trazas: [trazaAprobacion(estado, `${resumen} ${NOTA_PLAZOS}`)] };
  }
  const pasadas = e.actividades.filter(a => a.planned_date < hoyIso);
  const estados = pasadas.map(a => estadoActividad(a.planned_date, e.respaldos.get(a.activity_key), hoyIso));
  const ejecutadas = estados.filter(x => x === 'ejecutada').length;
  const vencidas = estados.filter(x => x === 'vencida').length;
  const vencidasGraves = estados.filter(x => x === 'vencida_grave').length;
  const estado: EstadoItem = vencidasGraves > 0 ? 'no_cumple' : vencidas > 0 ? 'en_proceso' : 'cumple';
  const total = e.actividades.length;
  const resumen = estado === 'cumple'
    ? (pasadas.length === 0
      ? `Versión ${e.aprobada.version} aprobada y firmada; todavía no hay actividades con fecha pasada (${plural(total, 'actividad', 'actividades')} en el cronograma).`
      : `Versión ${e.aprobada.version} aprobada y firmada; ${plural(ejecutadas, 'actividad con fecha pasada ejecutada', 'actividades con fecha pasada ejecutadas')} con respaldo (de ${total} en el cronograma).`)
    : estado === 'no_cumple'
      ? `${plural(vencidasGraves, 'actividad lleva', 'actividades llevan')} más de ${DIAS_TOLERANCIA_EJECUCION} días vencida${vencidasGraves === 1 ? '' : 's'} sin ejecutar (de ${total} en el cronograma).`
      : `${plural(vencidas, 'actividad vencida', 'actividades vencidas')} hace ${DIAS_TOLERANCIA_EJECUCION} días o menos, sin ejecutar todavía (de ${total} en el cronograma).`;
  return {
    estado, resumen, total, conFechaPasada: pasadas.length, ejecutadas, vencidas, vencidasGraves,
    trazas: [
      trazaAprobacion('cumple', `Versión ${e.aprobada.version} aprobada el ${fechaCorta(e.aprobada.approved_at)}.`),
      { metodo: 'programa_anual', norma: cfg.norma, articulo_o_tabla: articulo, umbral: pasadas.length, valor: ejecutadas,
        unidad: 'actividades con fecha pasada ejecutadas con respaldo', estado: estadoTraza(estado), fuente_doc: FUENTE_ANEXO1, nota: resumen },
      { metodo: 'programa_anual', norma: NORMA_PLAZOS, articulo_o_tabla: `Tolerancia para ejecutar una actividad vencida: ${DIAS_TOLERANCIA_EJECUCION} días`,
        umbral: 0, valor: vencidasGraves, unidad: `vencidas hace más de ${DIAS_TOLERANCIA_EJECUCION} días`,
        estado: vencidasGraves > 0 ? 'no_cumple' : 'cumple', fuente_doc: FUENTE_ANEXO1, nota: NOTA_PLAZOS },
    ],
  };
}

/** Riesgo psicosocial (casilla anterior "Registrado en SUT", ya sin pantalla): aviso si estaba marcada y no hay la
 *  constancia nueva del SUT del año. */
export const AVISO_SUT_ANTERIOR = 'Registro anterior marcado sin constancia: suba la constancia del SUT';
export const avisoSutAnterior = (marcadaAntes: boolean | null | undefined, conConstancia: boolean): string | null =>
  marcadaAntes && !conConstancia ? AVISO_SUT_ANTERIOR : null;

/** PO 25 y PO 27: constancia del SUT del programa del año en curso. */
export function resultadoSut(tipo: 'psicosocial' | 'sustancias', programa: FilaPrograma | null, sut: FilaSut | null, hoy: Date = new Date()): ResultadoPrograma {
  const cfg = CONFIG[tipo];
  const anio = hoy.getFullYear();
  const conConstancia = !!(programa && sut && sut.constancia_path);
  const estado: EstadoItem = conConstancia ? 'cumple' : 'pendiente';
  const resumen = conConstancia
    ? `Registrado en el SUT con el código ${sut!.registration_code} el ${fechaCorta(sut!.registration_date)}; constancia subida.`
    : programa ? `Falta registrar el ${cfg.corto.toLowerCase()} ${anio} en el SUT y subir la constancia.`
      : `No hay ${cfg.corto.toLowerCase()} del año ${anio}: créelo, regístrelo en el SUT y suba la constancia.`;
  return {
    estado, resumen, total: 0, conFechaPasada: 0, ejecutadas: 0, vencidas: 0, vencidasGraves: 0,
    trazas: [{ metodo: 'programa_anual_sut', norma: cfg.norma, articulo_o_tabla: `${(cfg.itemSut ?? '').replace('-', ' ')}: registro en el SUT del Ministerio del Trabajo`,
      umbral: 1, valor: conConstancia ? 1 : 0, unidad: `constancia del SUT del año ${anio}`, estado: conConstancia ? 'cumple' : 'por_validar',
      fuente_doc: FUENTE_ANEXO1, nota: resumen }],
  };
}

// ── Versión nueva ──
/** La versión aprobada se copia a un borrador con el número siguiente; las actividades conservan su clave. */
export function copiaParaVersionNueva(v: FilaVersion, actividades: FilaActividad[], numero: number, motivo: string) {
  return {
    version: { version: numero, objectives: v.objectives, scope_diagnosis: v.scope_diagnosis, methodology: v.methodology,
      responsibles: v.responsibles, resources: v.resources, settings: v.settings ?? {}, change_reason: motivo.trim() || null },
    actividades: ordenarActividades(actividades).map((a, i) => ({
      activity_key: a.activity_key, position: i, template_no: a.template_no, title: a.title, audience_kind: a.audience_kind,
      audience_label: a.audience_label, position_id: a.position_id, worker_id: a.worker_id, planned_date: a.planned_date,
      responsible: a.responsible, duration_hours: a.duration_hours, training_id: a.training_id, exam_type: a.exam_type, notes: a.notes,
      indicator: a.indicator ?? null, schedule_id: a.schedule_id ?? null,
    })),
  };
}

export const MOTIVO_MINIMO = 10;
